import { z } from 'zod'

/* =========================
   AUTH / LOGIN
========================= */
export const loginSchema = z.object({
  user_login: z.string().min(1, 'Client ID is required'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
  remember_me: z.boolean().optional().default(false),
})

export type LoginFormData = z.infer<typeof loginSchema>

/* =========================
   GENERIC USER
========================= */
export const userSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  user_login: z.string().min(1, 'Client ID is required'),
  email: z.string().email('Invalid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters').optional(),
  role: z.string().min(1, 'Role is required'),
})

export type UserFormData = z.infer<typeof userSchema>

/* =========================
   USER MANAGEMENT
========================= */
export const createUserSchema = z.object({
  // user_login === client_id
  user_login: z
    .string()
    .min(1, 'Client ID is required')
    .regex(/^[A-Z0-9_]+$/, 'Client ID must contain uppercase letters, numbers, and underscores'),

  user_email: z.string().email('Invalid email address'),

  user_pass: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[0-9]/, 'Password must contain at least one digit')
    .regex(/[@$!%*#?&]/, 'Password must contain at least one special character (@$!%*#?&)'),

  employee_id: z.string().optional(),
  first_name: z.string().min(1, 'First name is required'),
  last_name: z.string().min(1, 'Last name is required'),
  position: z.string().optional(),
  department: z.string().optional(),
  user_role: z.string().min(1, 'Role is required'),
  cluster: z.string().optional(),

  user_status: z.number().int().min(0).max(2).default(1), // 0 = Inactive, 1 = Active, 2 = Suspended
})

export const updateUserSchema = z.object({
  // immutable client scope
  user_login: z.string().min(1, 'Client ID is required'),

  user_email: z.string().email('Invalid email address'),

  user_pass: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[0-9]/, 'Password must contain at least one digit')
    .regex(/[@$!%*#?&]/, 'Password must contain at least one special character (@$!%*#?&)')
    .optional()
    .or(z.literal('')),

  employee_id: z.string().optional(),
  first_name: z.string().min(1, 'First name is required'),
  last_name: z.string().min(1, 'Last name is required'),
  position: z.string().optional(),
  department: z.string().optional(),
  user_role: z.string().min(1, 'Role is required'),
  cluster: z.string().optional(),

  user_status: z.number().int().min(0).max(2).default(1),
})

export type CreateUserFormData = z.infer<typeof createUserSchema>
export type UpdateUserFormData = z.infer<typeof updateUserSchema>

/* =========================
   ROLE MANAGEMENT
========================= */
export const createRoleSchema = z.object({
  name: z.string().min(1, 'Role name is required').max(255),
  active: z.boolean().default(true),
  permissions: z
    .record(
      z.string(),
      z.record(
        z.string(),
        z.record(z.string(), z.boolean())
      )
    )
    .refine(
      (permissions) =>
        Object.values(permissions).some((nav) =>
          Object.values(nav).some((perms) =>
            Object.values(perms).some(Boolean)
          )
        ),
      { message: 'Please select at least one permission' }
    ),
})

export const updateRoleSchema = createRoleSchema

export type CreateRoleFormData = z.infer<typeof createRoleSchema>
export type UpdateRoleFormData = z.infer<typeof updateRoleSchema>

/* =========================
   NAVIGATION
========================= */
export const navigationSchema = z.object({
  name: z.string().min(1),
  path: z.string().min(1),
  icon: z.string().optional(),
  parent_id: z.number().optional(),
  permissions: z.array(z.string()).optional(),
})

export const createNavigationSchema = z.object({
  name: z.string().min(1),
  slug: z.string().min(1),
  icon: z.string().optional(),
  parent_id: z.number().nullable().optional(),
  active: z.boolean().default(true),
  show_in_menu: z.boolean().default(true),
  application: z.enum(['auth', 'sima', 'support']).optional().nullable(),
})

export const updateNavigationSchema = createNavigationSchema

export type CreateNavigationFormData = z.infer<typeof createNavigationSchema>
export type UpdateNavigationFormData = z.infer<typeof updateNavigationSchema>
export type NavigationFormData = z.infer<typeof navigationSchema>

/* =========================
   CLIENT MANAGEMENT
========================= */
export const createClientSchema = z.object({
  code: z
    .string()
    .min(1)
    .max(255)
    .regex(/^[A-Z0-9_]+$/, 'Client code must be uppercase, numbers, underscore'),
  name: z.string().min(1).max(255),
  database_name: z
    .string()
    .min(1)
    .max(255)
    .regex(/^[a-z0-9_]+$/, 'Database name must be lowercase, numbers, underscore'),
  status: z.boolean().default(true),
  config: z
    .object({
      timezone: z.string().optional(),
      currency: z.string().optional(),
      date_format: z.string().optional(),
    })
    .optional()
    .nullable(),
  description: z.string().max(1000).optional().nullable(),
})

export const updateClientSchema = createClientSchema

export type CreateClientFormData = z.infer<typeof createClientSchema>
export type UpdateClientFormData = z.infer<typeof updateClientSchema>
